import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  InputAdornment,
  Collapse,
  Checkbox,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  AutoAwesome,
  Close,
  Delete,
  Edit,
  EmojiEvents,
  Search,
  SelectAll,
} from "@mui/icons-material";
import { phappsRequest } from "@/services/apiService";
import {
  API_PATHS,
  buildLeaderboardCompetitionPath,
  buildLeaderboardCompetitionPharmaciesPath,
} from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { DOCLINK_COLORS } from "@/config/colors";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const EMPTY_FORM = {
  name: "",
  description: "",
  startsAt: "",
  endsAt: "",
  superpowerWeekStart: "",
  isActive: true,
};

// ── sub-components ────────────────────────────────────────────────────────────

function CompetitionList({ competitions, loading, error, onSelect, onCreateNew }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
          Competitions
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={onCreateNew}
          sx={{
            textTransform: "none",
            backgroundColor: DOCLINK_COLORS.MID_PURPLE,
            fontWeight: 600,
          }}
        >
          New competition
        </Button>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : competitions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No competitions yet. Create one to get started.
        </Typography>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {competitions.map((c) => (
            <Box
              key={c.competitionId}
              onClick={() => onSelect(c)}
              sx={{
                p: 2,
                mb: 1.5,
                borderRadius: 2,
                border: `1px solid ${DOCLINK_COLORS.LIGHT_PURPLE}`,
                cursor: "pointer",
                transition: "background 0.15s",
                "&:hover": { bgcolor: DOCLINK_COLORS.LAVENDER_TINT ?? "#f3eeff" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Typography fontWeight={600} sx={{ flex: 1 }}>
                  {c.name}
                </Typography>
                <Chip
                  label={c.isActive ? "Active" : "Inactive"}
                  size="small"
                  color={c.isActive ? "success" : "default"}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {fmt(c.startsAt)} → {fmt(c.endsAt)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {c.pharmacyCount ?? 0} {c.pharmacyCount === 1 ? "pharmacy" : "pharmacies"}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function CompetitionForm({ initial, onSave, onCancel, saving, saveError, isCreate }) {
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestError, setSuggestError] = useState(null);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSuggest = async () => {
    if (!form.startsAt || !form.endsAt) {
      setSuggestError("Set the start and end dates first so Claude has context.");
      return;
    }
    setSuggesting(true);
    setSuggestions([]);
    setSuggestError(null);
    try {
      const res = await phappsRequest(
        API_PATHS.LEADERBOARD_COMPETITIONS_SUGGEST_NAME,
        "POST",
        {
          startsAt: form.startsAt,
          endsAt: form.endsAt,
          context: form.description.trim() || null,
        }
      );
      setSuggestions(res?.suggestions ?? []);
    } catch (err) {
      setSuggestError(getAdminApiErrorMessage(err, "Claude couldn't generate suggestions right now."));
    } finally {
      setSuggesting(false);
    }
  };

  const pickSuggestion = (name) => {
    setForm((f) => ({ ...f, name }));
    setSuggestions([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      startsAt: form.startsAt,
      endsAt: form.endsAt,
      superpowerWeekStart: form.superpowerWeekStart || null,
      isActive: form.isActive,
    };
    if (isCreate) {
      payload.scope = "group";
      payload.chemistIds = [];
    }
    onSave(payload);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {saveError && <Alert severity="error">{saveError}</Alert>}

      {/* Name field + suggest button */}
      <Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={set("name")}
            required
            fullWidth
            size="small"
            inputProps={{ maxLength: 200 }}
          />
          <Tooltip title="Ask Claude to suggest fun competition names based on the dates and description below">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSuggest}
                disabled={suggesting}
                startIcon={suggesting ? <CircularProgress size={13} color="inherit" /> : <AutoAwesome fontSize="small" />}
                sx={{
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  borderColor: "#7c3aed",
                  color: "#7c3aed",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#6d28d9", bgcolor: "#f5f3ff" },
                }}
              >
                {suggesting ? "Asking Claude…" : "Suggest ✨"}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Suggestions panel */}
        <Collapse in={suggestions.length > 0 || !!suggestError}>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #e9d5ff",
              bgcolor: "#faf5ff",
            }}
          >
            {suggestError ? (
              <Typography variant="caption" color="error">{suggestError}</Typography>
            ) : (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#7c3aed", display: "block", mb: 1 }}>
                  Claude's suggestions — click one to use it:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {suggestions.map((s, i) => (
                    <Chip
                      key={i}
                      label={s}
                      size="small"
                      onClick={() => pickSuggestion(s)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: "#ede9fe",
                        color: "#4c1d95",
                        fontWeight: 600,
                        "&:hover": { bgcolor: "#ddd6fe" },
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      </Box>

      <TextField
        label="Description / context (optional)"
        value={form.description}
        onChange={set("description")}
        fullWidth
        size="small"
        multiline
        rows={2}
        inputProps={{ maxLength: 1000 }}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Starts"
          type="date"
          value={form.startsAt}
          onChange={set("startsAt")}
          required
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Ends"
          type="date"
          value={form.endsAt}
          onChange={set("endsAt")}
          required
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <TextField
        label="Superpower week start (optional)"
        type="date"
        value={form.superpowerWeekStart}
        onChange={set("superpowerWeekStart")}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        helperText="Leave blank for no superpower week. End date is calculated automatically (start + 6 days)."
      />

      <FormControlLabel
        control={
          <Switch
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: DOCLINK_COLORS.MID_PURPLE },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: DOCLINK_COLORS.MID_PURPLE,
              },
            }}
          />
        }
        label="Active"
      />

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", pt: 1 }}>
        <Button onClick={onCancel} size="small" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{ textTransform: "none", backgroundColor: DOCLINK_COLORS.MID_PURPLE }}
        >
          {isCreate ? "Create competition" : "Save changes"}
        </Button>
      </Box>
    </Box>
  );
}

function PharmacyManager({ competitionId }) {
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);
  // Set of ChemistIDs selected via checkboxes
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchPharmacies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(
        buildLeaderboardCompetitionPharmaciesPath(competitionId),
        "GET"
      );
      setPharmacies(res?.data ?? []);
    } catch {
      setError("Failed to load pharmacies");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => { fetchPharmacies(); }, [fetchPharmacies]);

  const handleSearch = async () => {
    const q = searchTerm.trim();
    if (!q) return;
    try {
      setSearching(true);
      setAddError(null);
      setSelectedIds(new Set());
      const res = await phappsRequest(API_PATHS.SEARCH_HALAXY_FOR_CHEMIST, "POST", {
        HaloSearch: q,
      });
      setSearchResults(res?.chemists ?? []);
    } catch {
      setAddError("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // Eligible = not already linked
  const eligibleResults = searchResults.filter(
    (r) => !pharmacies.some((p) => p.chemistId === r.ChemistID)
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allEligibleSelected =
    eligibleResults.length > 0 &&
    eligibleResults.every((r) => selectedIds.has(r.ChemistID));

  const toggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(eligibleResults.map((r) => r.ChemistID)));
    }
  };

  const handleAddSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try {
      setAdding(true);
      setAddError(null);
      await phappsRequest(
        buildLeaderboardCompetitionPharmaciesPath(competitionId),
        "POST",
        { chemistIds: ids }
      );
      setSearchResults([]);
      setSearchTerm("");
      setSelectedIds(new Set());
      await fetchPharmacies();
    } catch {
      setAddError("Failed to add pharmacies");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (chemistId) => {
    try {
      setAddError(null);
      await phappsRequest(
        buildLeaderboardCompetitionPharmaciesPath(competitionId),
        "DELETE",
        { chemistIds: [chemistId] }
      );
      await fetchPharmacies();
    } catch {
      setAddError("Failed to remove pharmacy");
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: DOCLINK_COLORS.DARK_PURPLE }}>
        Manage pharmacies
      </Typography>

      {addError && <Alert severity="error" sx={{ mb: 1 }}>{addError}</Alert>}

      {/* Search bar */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        <TextField
          placeholder="Search pharmacy name…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleSearch}
          disabled={searching}
          sx={{ textTransform: "none", whiteSpace: "nowrap", borderColor: DOCLINK_COLORS.MID_PURPLE, color: DOCLINK_COLORS.DARK_PURPLE }}
        >
          {searching ? <CircularProgress size={16} /> : "Search"}
        </Button>
      </Box>

      {/* Search results */}
      {searchResults.length > 0 && (
        <Box sx={{ mb: 2, border: `1px solid ${DOCLINK_COLORS.LIGHT_PURPLE}`, borderRadius: 1, overflow: "hidden" }}>
          {/* Toolbar: select-all + add selected */}
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#f5f0ff",
              borderBottom: `1px solid ${DOCLINK_COLORS.LIGHT_PURPLE}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {eligibleResults.length > 0 && (
                <Tooltip title={allEligibleSelected ? "Deselect all" : "Select all"}>
                  <Checkbox
                    size="small"
                    checked={allEligibleSelected}
                    indeterminate={selectedIds.size > 0 && !allEligibleSelected}
                    onChange={toggleSelectAll}
                    sx={{ p: 0.5, color: DOCLINK_COLORS.MID_PURPLE, "&.Mui-checked": { color: DOCLINK_COLORS.MID_PURPLE } }}
                  />
                </Tooltip>
              )}
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {searchResults.length} result{searchResults.length !== 1 ? "s" : ""}
                {eligibleResults.length < searchResults.length &&
                  ` · ${searchResults.length - eligibleResults.length} already added`}
              </Typography>
            </Box>

            {selectedIds.size > 0 && (
              <Button
                size="small"
                variant="contained"
                onClick={handleAddSelected}
                disabled={adding}
                startIcon={adding ? <CircularProgress size={13} color="inherit" /> : <Add />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  backgroundColor: DOCLINK_COLORS.MID_PURPLE,
                  "&:hover": { backgroundColor: DOCLINK_COLORS.DARK_PURPLE },
                }}
              >
                {adding ? "Adding…" : `Add ${selectedIds.size} selected`}
              </Button>
            )}
          </Box>

          <List dense disablePadding sx={{ maxHeight: 220, overflowY: "auto" }}>
            {searchResults.map((r) => {
              const alreadyLinked = pharmacies.some((p) => p.chemistId === r.ChemistID);
              const isSelected = selectedIds.has(r.ChemistID);
              return (
                <ListItem
                  key={r.ChemistID}
                  divider
                  disablePadding
                  sx={{
                    px: 1,
                    bgcolor: isSelected ? "#f5f0ff" : "transparent",
                    opacity: alreadyLinked ? 0.55 : 1,
                  }}
                >
                  {!alreadyLinked && (
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleSelect(r.ChemistID)}
                      sx={{ color: DOCLINK_COLORS.MID_PURPLE, "&.Mui-checked": { color: DOCLINK_COLORS.MID_PURPLE } }}
                    />
                  )}
                  <ListItemText
                    primary={r.ChemistName}
                    sx={{ pl: alreadyLinked ? 4.5 : 0 }}
                  />
                  <ListItemSecondaryAction>
                    {alreadyLinked ? (
                      <Chip label="Already added" size="small" color="success" />
                    ) : (
                      <Tooltip title="Add just this pharmacy">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            setSelectedIds(new Set([r.ChemistID]));
                            try {
                              setAdding(true);
                              setAddError(null);
                              await phappsRequest(
                                buildLeaderboardCompetitionPharmaciesPath(competitionId),
                                "POST",
                                { chemistIds: [r.ChemistID] }
                              );
                              setSearchResults((prev) => prev.filter((x) => x.ChemistID !== r.ChemistID));
                              setSelectedIds(new Set());
                              await fetchPharmacies();
                            } catch {
                              setAddError("Failed to add pharmacy");
                            } finally {
                              setAdding(false);
                            }
                          }}
                          sx={{ color: DOCLINK_COLORS.MID_PURPLE }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      <Divider sx={{ mb: 1.5 }} />

      {/* Linked pharmacies */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {loading ? "Loading…" : `${pharmacies.length} linked ${pharmacies.length === 1 ? "pharmacy" : "pharmacies"}`}
      </Typography>
      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
      {!loading && pharmacies.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No pharmacies linked yet.</Typography>
      ) : (
        <List dense sx={{ maxHeight: 280, overflowY: "auto" }}>
          {pharmacies.map((p) => (
            <ListItem key={p.chemistId} divider>
              <ListItemText primary={p.name || `Chemist #${p.chemistId}`} />
              <ListItemSecondaryAction>
                <Tooltip title="Remove from competition">
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(p.chemistId)}
                    sx={{ color: "error.main" }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function LeaderboardManageModal({ open, onClose }) {
  const [competitions, setCompetitions] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  // view: 'list' | 'create' | 'detail'
  const [view, setView] = useState("list");
  const [selected, setSelected] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const fetchCompetitions = useCallback(async () => {
    try {
      setListLoading(true);
      setListError(null);
      const res = await phappsRequest(API_PATHS.LEADERBOARD_COMPETITIONS_ALL, "GET");
      setCompetitions(res?.data ?? []);
    } catch {
      setListError("Failed to load competitions");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchCompetitions();
      setView("list");
      setSelected(null);
      setEditMode(false);
    }
  }, [open, fetchCompetitions]);

  const handleSelect = (competition) => {
    setSelected(competition);
    setEditMode(false);
    setSaveError(null);
    setView("detail");
  };

  const handleCreateNew = () => {
    setSelected(null);
    setSaveError(null);
    setView("create");
  };

  const handleBack = () => {
    setView("list");
    setSelected(null);
    setEditMode(false);
    setSaveError(null);
  };

  const handleCreate = async (payload) => {
    try {
      setSaving(true);
      setSaveError(null);
      await phappsRequest(API_PATHS.LEADERBOARD_COMPETITIONS_CREATE, "POST", payload);
      await fetchCompetitions();
      setView("list");
    } catch (err) {
      setSaveError(err?.message ?? "Failed to create competition");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    try {
      setSaving(true);
      setSaveError(null);
      await phappsRequest(
        buildLeaderboardCompetitionPath(selected.competitionId),
        "PUT",
        payload
      );
      await fetchCompetitions();
      // Update selected too
      setSelected((s) => ({ ...s, ...payload, isActive: payload.isActive }));
      setEditMode(false);
    } catch (err) {
      setSaveError(err?.message ?? "Failed to save competition");
    } finally {
      setSaving(false);
    }
  };

  const formInitial = selected
    ? {
        name: selected.name ?? "",
        description: selected.description ?? "",
        startsAt: selected.startsAt?.slice(0, 10) ?? "",
        endsAt: selected.endsAt?.slice(0, 10) ?? "",
        superpowerWeekStart: selected.superpowerWeekStart?.slice(0, 10) ?? "",
        isActive: Boolean(selected.isActive),
      }
    : EMPTY_FORM;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <EmojiEvents sx={{ color: DOCLINK_COLORS.MID_PURPLE }} />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: DOCLINK_COLORS.DARK_PURPLE }}>
          Manage leaderboard competitions
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0, minHeight: 500 }}>
        {view === "list" && (
          <Box sx={{ p: 3, height: "100%" }}>
            <CompetitionList
              competitions={competitions}
              loading={listLoading}
              error={listError}
              onSelect={handleSelect}
              onCreateNew={handleCreateNew}
            />
          </Box>
        )}

        {view === "create" && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton size="small" onClick={handleBack}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" fontWeight={700} sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}>
                New competition
              </Typography>
            </Box>
            <CompetitionForm
              initial={EMPTY_FORM}
              isCreate
              onSave={handleCreate}
              onCancel={handleBack}
              saving={saving}
              saveError={saveError}
            />
          </Box>
        )}

        {view === "detail" && selected && (
          <Box sx={{ display: "flex", height: "100%", minHeight: 500 }}>
            {/* Left: info + edit */}
            <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={handleBack}>
                  <ArrowBack />
                </IconButton>
                <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: DOCLINK_COLORS.DARK_PURPLE }}>
                  {selected.name}
                </Typography>
                {!editMode && (
                  <Tooltip title="Edit competition">
                    <IconButton size="small" onClick={() => { setEditMode(true); setSaveError(null); }}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {editMode ? (
                <CompetitionForm
                  initial={formInitial}
                  isCreate={false}
                  onSave={handleUpdate}
                  onCancel={() => { setEditMode(false); setSaveError(null); }}
                  saving={saving}
                  saveError={saveError}
                />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip
                      label={selected.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={selected.isActive ? "success" : "default"}
                    />
                    {selected.scope && (
                      <Chip label={`Scope: ${selected.scope}`} size="small" variant="outlined" />
                    )}
                  </Box>

                  {selected.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selected.description}
                    </Typography>
                  )}

                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block" }}>
                      Period
                    </Typography>
                    <Typography variant="body2">
                      {fmt(selected.startsAt)} → {fmt(selected.endsAt)}
                    </Typography>
                  </Box>

                  {selected.superpowerWeekStart && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block" }}>
                        Superpower week
                      </Typography>
                      <Typography variant="body2">
                        {fmt(selected.superpowerWeekStart)} → {fmt(selected.superpowerWeekEnd)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Right: pharmacies */}
            <Box sx={{ width: 380, flexShrink: 0, p: 3, overflowY: "auto" }}>
              <PharmacyManager competitionId={selected.competitionId} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
